import React, { useEffect, useState } from 'react';
import { Form, Formik, FormikProps, FieldArray, FieldArrayRenderProps } from 'formik';
import { useDispatch } from 'react-redux';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { useEpiClergySettings, useEpiClergySettingsLoaded } from '../../hooks/useEpiClergySettings';
import { ClergyFilters, ClergyAdvancedSearchFormValues, AdvancedSearchFormValidationSchema, ClergyLocationQuickSearchResult } from './clergy-model';
import { useApiResponseError } from '../../hooks/useApiError';
import { FormWrapper } from '../form-wrapper';
import { FormField } from '../../../../TCSMApp/global/components/form-element/form-field';
import { Wysiwyg } from '../wysiwyg';
import { GetBishopOptions, GetCanonicalResidence, mapCanonicalResidence, QuickSearchByLocation } from '../../ducks/clergy-api';
import { ApiSmallResponse } from '../../models/api-model';
import { replaceAll } from '../../../../TCSMApp/global/helpers/string-helpers';
import { DelayExecute } from '../../helpers/delay';

interface Props {
	performSearch: (filters: ClergyFilters) => void;
	searchFilters: ClergyFilters;
	clearForm: () => void;
}

function ClergySearchAdvanced(props: Props) {
	const { performSearch, searchFilters, clearForm } = props;
	const dispatch = useDispatch();
	const [canonicalResidenceOptions, setCanonicalResidenceOptions] = useState(null);
	const clergySettingsLoaded = useEpiClergySettingsLoaded();
	const clergySettings = useEpiClergySettings();
	const apiErrors = useApiResponseError();

	const bishopOptions = GetBishopOptions(clergySettings);
	const canonicalResidenceSelectedOptions: string[] = searchFilters?.c_res?.length > 0 ? searchFilters?.c_res as any : [];

	useEffect(() => {
		if (clergySettingsLoaded) {
			dispatch(GetCanonicalResidence(
				(res) => {
					setCanonicalResidenceOptions(res?.data
						.sort((a, b) => { return mapCanonicalResidence(a?.short_name, clergySettings?.canonicalResidanceMapping).localeCompare(mapCanonicalResidence(b?.short_name, clergySettings?.canonicalResidanceMapping)); })
						.map(item => { return { value: item?.party_id, text: mapCanonicalResidence(item?.short_name, clergySettings?.canonicalResidanceMapping) }; }));
				},
			));
		}
	}, [clergySettingsLoaded]);

	const initialLocationResults: ApiSmallResponse<ClergyLocationQuickSearchResult> = {
		results: [],
		searchValue: '',
	};

	const initialValues: ClergyAdvancedSearchFormValues = {
		first_name: searchFilters?.first_name,
		last_name: searchFilters?.last_name,
		location: searchFilters?.location,
		zip_code: searchFilters?.zip_code,
		canonicalResidence: searchFilters?.c_res,
		is_bishop: searchFilters?.is_bishop,
	};

	const [quickSearchLocationResults, setQuickSearchLocationResults] = useState(initialLocationResults);
	const [quickSearchLocationVisible, setQuickSearchLocationVisible] = useState(false);
	const [quickSearchLocationInProgress, setQuickSearchLocationInProgress] = useState(false);

	if (!clergySettingsLoaded || !clergySettings) {
		return <LoadingCircle />;
	}

	function CloseAutocomplete(flagCallback: () => void) {
		if (!flagCallback) return null;

		setTimeout(() => {
			flagCallback();
		}, 250);
	}

	function QuickSearchCityResult(searchCity: string, data) {
		setQuickSearchLocationResults(
			{
				...data,
				searchValue: searchCity,
			});
		setQuickSearchLocationInProgress(false);
	}

	function QuickSearchLocation(searchValue: string) {
		if (searchValue?.length > 1) {
			setQuickSearchLocationVisible(true);
			setQuickSearchLocationInProgress(true);
			dispatch(QuickSearchByLocation(searchValue, (remoteData) => QuickSearchCityResult(searchValue, remoteData)));
		}
	}

	function OnFieldChange(fieldName: string, value: string, formProps: FormikProps<ClergyAdvancedSearchFormValues>) {
		formProps.setFieldTouched(fieldName, true);
		formProps.setFieldValue(fieldName, value);
	}

	function OnCityChanged(e, formProps: FormikProps<ClergyAdvancedSearchFormValues>) {		
		OnFieldChange('location', e?.target?.value, formProps);
		DelayExecute(
            ()=>{                 
                QuickSearchLocation(e?.target?.value);
            },
            'cityOrCountry');		
	}

	function SelectResultByLocation(selectedLocation: ClergyLocationQuickSearchResult) {
		if (!selectedLocation) return;

		performSearch({ location: selectedLocation?.location?.trim() });
	}

	function SelectAllResultByLocation(requestSearchValue: string) {
		if (quickSearchLocationResults?.results?.length > 0) {

			performSearch({ location: requestSearchValue });
		}
	}

	function OnSubmit(submitValues: ClergyAdvancedSearchFormValues) {
		performSearch({
			first_name: submitValues?.first_name,
			last_name: submitValues?.last_name,
			location: submitValues?.location,
			zip_code: submitValues?.zip_code,
			c_res: submitValues?.canonicalResidence,
			is_bishop: submitValues?.is_bishop,
		});
	}

	function OnCancel(canelFormProps: FormikProps<ClergyAdvancedSearchFormValues>) {
		canelFormProps.resetForm({});
		clearForm();
		setQuickSearchLocationResults(initialLocationResults);
		window.scrollTo(0, 0);
	}

	function RenderAutocompleteNoResults(){
        return (<ul className='quickfind-autocomplete-list'>
                        <li className='quickfind-autocomplete-list-item'>
                            <div className='quickfind-autocomplete-link'>
                                <span className='quickfind-autocomplete-link-detail'>{clergySettings?.noResultsAutocompleteMessage || 'Sorry, no results were found.'}</span>
                            </div>
                        </li>
                    </ul>);
    }

	function RenderQuickSearchLocationsAutocomplete() {
		if (quickSearchLocationVisible && !quickSearchLocationInProgress) {
            if (quickSearchLocationResults?.results?.length > 0){
				return (<ul className='quickfind-autocomplete-list'>

					{quickSearchLocationResults.results.map(
						(item) =>
							<li>
								<button className='quickfind-autocomplete-link' type='button' onClick={() => SelectResultByLocation(item)} key={item?.location?.trim()}>{item?.location?.trim()}</button>
							</li>,
					)}

					<li className='quickfind-autocomplete-divider'>
						<button className='quickfind-autocomplete-link' type='button' onClick={() => SelectAllResultByLocation(quickSearchLocationResults?.searchValue)}>{replaceAll(clergySettings?.viewAllResultsTemplate, '{0}', `${quickSearchLocationResults?.total_results}`)}</button>
					</li>
				</ul>);
			}

			return (<RenderAutocompleteNoResults />);
        }

		return null;
	}

	function FormFooter() {
		if (!clergySettings?.termsOfUseMessage) return null;

		return (
			<Wysiwyg content={clergySettings?.termsOfUseMessage} />
		);
	}

	function OnCanonicalResidenceChange(e, fieldArrayProps: FieldArrayRenderProps) {
		const { value } = e.target;
		if (e.target.checked) {
			canonicalResidenceSelectedOptions.push(value);
			fieldArrayProps.push(value);
		} else {
			const itemIndex = canonicalResidenceSelectedOptions.indexOf(value);
			if (itemIndex > -1) {
				fieldArrayProps.remove(itemIndex);
				canonicalResidenceSelectedOptions.splice(itemIndex, 1);
			}
		}
	}

	function IsFilterEmpty(formProps: FormikProps<ClergyAdvancedSearchFormValues>): boolean {
		return formProps && formProps.values &&
			!formProps.values.first_name &&
			!formProps.values.last_name &&
			!formProps.values.location &&
			!(formProps.values.canonicalResidence?.length > 0) &&
			!formProps.values.is_bishop &&
			!formProps.values.zip_code;
	}

    return (
        <>
            <h3 className='ecd-page-content-title'>
                {clergySettings?.advancedSearchTitle}
            </h3>
            <div className='ecd-page-content-section'>
                <p>
                    <strong>{clergySettings?.advancedFormInstructionMessage}</strong>
                </p>
                <Formik
                    initialValues={initialValues}
                    onSubmit={OnSubmit}
                    validationSchema={AdvancedSearchFormValidationSchema(clergySettings)}
                    enableReinitialize
                    initialErrors={apiErrors}
                >
                    {(formProps: FormikProps<ClergyAdvancedSearchFormValues>) => (
                        <Form>
                            <FormWrapper
                                formikProps={formProps}
                                submitText={clergySettings.submitFormButton}
                                cancelText={clergySettings.clearFormButton}
                                submitDisabled={IsFilterEmpty(formProps)}
                                onCancel={() => { OnCancel(formProps); }}
                                preventButtonDisableValidation
                                formFooter={FormFooter()}
                            >
                                <div className='form-group'>
                                <div className='quickfind-parish-section-columns'>                                 
                                    <FormField
                                        name='first_name'
                                        label={clergySettings?.firstNameLabel || 'First Name'}
                                        component='input'
                                        touched={formProps.touched}
                                        errors={formProps.errors}
                                    />
                                    <FormField
                                        name='last_name'
                                        label={clergySettings?.lastNameLabel || 'Last Name'}
                                        component='input'
                                        touched={formProps.touched}
                                        errors={formProps.errors}
                                    />
                                </div>
                                <div className='quickfind-parish-section-columns'>                                 
                                    <div className=' form-item quickfind-autocomplete-container'>
                                        <FormField
                                            name='location'
                                            label={clergySettings?.cityOrCountryLabel || 'Enter City or Country'}
                                            component='input'
                                            touched={formProps.touched}
                                            errors={formProps.errors}
                                            onChange={(eventCityChanged) => OnCityChanged(eventCityChanged, formProps)}
                                            onBlur={()=> CloseAutocomplete(() => setQuickSearchLocationVisible(false))}                                            
                                        />
                                        <RenderQuickSearchLocationsAutocomplete />
                                    </div>
                                    <FormField
                                        name='zip_code'
                                        label={clergySettings?.zipCodeLabel || 'Zip Code'}
                                        component='input'
                                        touched={formProps.touched}
                                        errors={formProps.errors}
                                    />
                                </div>
                                    <div className='form-item-item'>                                        
                                        <FieldArray
                                            name='canonicalResidence'
                                            render={(fieldArrayProps) => (
                                                <>
                                                    <label htmlFor='canonicalResidence'>                                                        
                                                            {clergySettings.canonicalResidenceLabel}                                                        
                                                    </label>
                                                    <div className='quickfind-scroll-container'>
                                                        {
                                                            canonicalResidenceOptions?.map(option => {
                                                                return (
                                                                    <FormField
                                                                        id={option.value}
                                                                        name='canonicalResidence'
                                                                        label={option.text}
                                                                        component='checkBox'
                                                                        touched={formProps.touched}
                                                                        errors={formProps.errors}
                                                                        value={option.value}
                                                                        checked={formProps.values.canonicalResidence?.includes(option.value)}
                                                                        checkBoxGroup
                                                                        onChange={(e) => OnCanonicalResidenceChange(e, fieldArrayProps)}
                                                                    />
                                                                );
                                                            })
                                                        }
                                                    </div>
                                                </>
                                            )}
                                        />                                        
                                    </div>
                                    <div className='form-item-item'>
                                        <FormField
                                            name='is_bishop'
                                            label={clergySettings?.bishopLabel || 'Bishop'}
                                            component='input'
                                            touched={formProps.touched}
                                            errors={formProps.errors}
                                            options={bishopOptions}
                                            select
                                            />
                                    </div>
                                </div>
                            </FormWrapper>
                        </Form>
                    )}
                </Formik >
            </div>
            <div className='ecd-page-content-section'>
                <Wysiwyg content={clergySettings?.postFormMessage} />
            </div>
        </>
    );
}

export default ClergySearchAdvanced;
